<template>
    <el-dialog v-model="dialog" @close="closeDialog" :width="500" title="Crop Image" center>
        <img v-if="resultPreview && result" :src="result" style="max-width: 100%" />
        <cropper v-else ref="cropperRef" :stencil-props="stencilProps" :src="img" />

        <template #footer>
            <el-button :disabled="loading" @click="dialog = false">Cancel</el-button>
            <el-button :disabled="loading" type="info" @click="crop">
                <i v-if="resultPreview" class="ti ti-reload"></i>
                <div v-else>Crop</div>
            </el-button>
            <el-button v-if="resultPreview" :disabled="loading" :loading="loading" type="success" @click="uploadFile">
                Confirm
            </el-button>
        </template>
    </el-dialog>

    <el-form-item :label="label" :prop="prop" label-position="top">
        <el-upload
            ref="upload"
            :before-upload="beforeUpload"
            :on-change="handleSelectFileChange"
            class="w-100"
            :class="{ 'upload-error': hasError }"
            drag
            :limit="1"
            :auto-upload="false"
            :file-list="fileList"
        >
            <div v-if="displayImage" class="d-flex justify-center">
                <img :src="displayImage" width="300" />
            </div>

            <i v-else style="font-size: 40pt" class="ti ti-cloud-up text-primary"></i>
            <div class="fw-semibold">
                <div class="fs-6 text-gray-700">
                    Drop image file here or <a href="#" class="me-1">click to upload</a>
                </div>
            </div>

            <template #tip>
                <div>
                    <span class="el-upload__tip text-red"> limit 1 image, new file will cover the old file </span>
                    (less than 10MB)
                </div>
            </template>
        </el-upload>

        <div v-if="hasError" class="text-red text-sm mt-1">Please upload an image.</div>
    </el-form-item>
</template>

<script lang="ts" setup>
    import { ref, computed } from 'vue'
    import { ElMessage } from 'element-plus'
    import { Cropper } from 'vue-advanced-cropper'
    import 'vue-advanced-cropper/dist/style.css'
    import type { UploadFile, UploadInstance, UploadUserFile } from 'element-plus'
    // import axios from '@/utils/apiClient'
    import axios from '@/utils/apiClient'
    import config from '@/config'

    const emit = defineEmits(['update:modelValue', 'uploaded'])
    const baseURL = config.baseURL

    const props = defineProps({
        modelValue: {
            type: String,
            default: null,
        },
        imageUrl: {
            // ✅ New prop
            type: String,
            default: null,
        },
        folder: {
            type: String,
            default: 'uploads',
        },
        prop: {
            type: String,
            default: null,
        },
        private: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: 'Upload File',
        },
        stencilProps: {
            type: Object,
            default: () => ({
                autoCropArea: 1,
                guides: true,
                center: true,
                highlight: true,
                dragMode: 'move',
                cropBoxMovable: true,
                cropBoxResizable: true,
                toggleDragModeOnDblclick: true,
            }),
        },
    })

    const upload = ref<UploadInstance>()
    const fileList = ref<UploadUserFile[]>([])
    const returnFilename = ref(null)
    const img = ref<string | null>(null)
    const result = ref<string | null>(null)
    const dialog = ref(false)
    const cropperRef = ref<any>(null)
    const resultPreview = ref(false)
    const loading = ref(false)
    const hasError = ref(false)

    const beforeUpload = (file: File) => {
        const isImage = file.type.startsWith('image/')
        if (!isImage) {
            ElMessage.error('Only image files are allowed')
            return false
        }
        if (file.size > 1024 * 1024 * 10) {
            ElMessage.error('File size cannot exceed 10MB')
            return false
        }
        return true
    }

    const closeDialog = () => {
        clearFileList()
        result.value = null
        resultPreview.value = false
        dialog.value = false
    }

    const clearFileList = () => {
        fileList.value = []
    }

    const handleSelectFileChange = (file: UploadFile, list: UploadUserFile[]) => {
        if (file.raw) {
            hasError.value = false
            fileList.value = list
            img.value = URL.createObjectURL(file.raw)
            dialog.value = true
        }
    }

    const validateUpload = () => {
        if (fileList.value.length === 0 && !props.modelValue) {
            hasError.value = true
            ElMessage.error('Please upload an image.')
            return false
        }
        hasError.value = false
        return true
    }

    const uploadFile = async () => {
        if (!validateUpload()) return
        try {
            loading.value = true
            const formData = new FormData()
            formData.append('file', result.value!)
            formData.append('folder', props.folder)
            formData.append('privateFile', props.private.toString())

            const response = await axios.post('photouploader', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            })

            if (response.status === 200) {
                ElMessage.success('Upload Success')
                const filename = response.data.fileName
                emit('update:modelValue', filename)
                emit('uploaded', filename)
                returnFilename.value = filename
                dialog.value = false
            }
        } catch (error) {
            console.error(error)
            ElMessage.error('Failed to upload file')
        } finally {
            loading.value = false
        }
    }

    const crop = () => {
        if (resultPreview.value) {
            result.value = null
            resultPreview.value = false
            return
        }

        const { canvas } = cropperRef.value.getResult()
        result.value = canvas.toDataURL()
        resultPreview.value = true
    }

    const displayImage = computed(() => {
        if (props.imageUrl) return baseURL + props.imageUrl
        if (props.modelValue) return baseURL + props.modelValue
        return null
    })
</script>

<style scoped>
    .upload-error .el-upload-dragger {
        border: 1px solid red !important;
    }
</style>
